// react and mui stuff
import { useContext, useEffect, useState } from "react"
import Markdown from "markdown-to-jsx"
import { UserContext } from "../utils/contexts"
import { ToastContext } from "../components/toast"

import Button from "@mui/joy/Button"
import Card from "@mui/joy/Card"
import CardActions from "@mui/joy/CardActions"
import CardContent from "@mui/joy/CardContent"
import Typography from "@mui/joy/Typography"
import Skeleton from "@mui/joy/Skeleton"
import FormControl from "@mui/joy/FormControl"
import FormLabel from "@mui/joy/FormLabel"
import IconButton from "@mui/joy/IconButton"
import Input from "@mui/joy/Input"
import Modal from "@mui/joy/Modal"
import ModalClose from "@mui/joy/ModalClose"
import ModalDialog from "@mui/joy/ModalDialog"
import Textarea from "@mui/joy/Textarea"

import Delete from "@mui/icons-material/Delete"
import FirstPage from "@mui/icons-material/FirstPage"
import LastPage from "@mui/icons-material/LastPage"
import ModeEdit from "@mui/icons-material/ModeEdit"
import NavigateBefore from "@mui/icons-material/NavigateBefore"
import NavigateNext from "@mui/icons-material/NavigateNext"


// custom components
import { MetaProps } from "../components/metaprops"

// data and stuff
import tag from "../types/tag"
import placeholderTags from "../data/placeholdertags"
import { setData } from "../utils/setdata"
import { imgSrc, tagAPI } from "../utils/backend"

type attachment = { src: string, mime: string | null }

type EditTagModalProps = {
  modifying: tag | undefined
  setModifying: Function
  submitFunction: (e: any, attachment: attachment) => any
}

function EditTagModal({ modifying, setModifying, submitFunction }: EditTagModalProps) {
  const { setToast } = useContext(ToastContext);
  const [attachment, setAttachment] = useState<attachment>({ src: "", mime: "" })

  useEffect(() => {
    if (modifying?.attachment) {
      setAttachment({
        mime: modifying.attachmentMime,
        src: imgSrc(`tags/${modifying.id}`)
      })
    } else {
      setAttachment({ mime: "", src: "" })
    }
  }, [modifying])
  return (
    <Modal open={!!modifying} onClose={() => setModifying(undefined)}>
      <ModalDialog>
        <ModalClose/>
        {modifying && <>
          Edit Tag
          <form onSubmit={e => submitFunction(e, attachment)}>
            <FormControl required>
              <FormLabel>Tag Name</FormLabel>
              <Input defaultValue={modifying.tag} name="tag" required/>
            </FormControl>
            
            <FormControl>
              <FormLabel>Tag Response</FormLabel>
              <Textarea defaultValue={modifying.response ?? ""} name="response" minRows={2}/>
            </FormControl>

            <div style={{ display: "flex", flexDirection: "column", marginBottom: "20px"}}>
              <FormLabel>Attachment</FormLabel>
              <AttachmentDisplay tag={modifying} path={attachment.src} mime={attachment.mime}/>
              {!attachment.src && "None"}
              {attachment.src && <Button color="danger" onClick={() => {
                setAttachment({ mime: "", src: "" });
                const element = document.getElementsByName("file").item(0) as any as HTMLElement & { value: string | null }
                if (element) element.value = ""
              }}>Remove Attachment</Button>}
            </div>
            <FormControl>
              <FormLabel>Set Attachment</FormLabel>
              <input type="file" name="file" onChange={async (e) => {
                const f = e.currentTarget.files?.item(0)
                if (f) {
                  if (f.size >= 7 * 1024 * 1024) {
                    e.preventDefault()
                    e.currentTarget.value = "";
                    setToast({ text: "Files must be less than 7MB.", color: "warning" })
                    return;
                  }

                  const  reader = new FileReader();
                  
                  reader.onloadend = () => {
                    if (modifying && reader.result) {
                      const name = f.name.split(".");
                      if (name.length > 0) name.pop();

                      setAttachment({ mime: f.type, src: reader.result.toString() })
                    }
                  }

                  reader.readAsDataURL(f)
                }
              }}/>
            </FormControl>

            <br/>

            <div style={{ display: "flex", justifyContent: "space-between"}}>
              <Button type="submit">Save</Button>
              <Button color="warning" variant="outlined" onClick={() => setModifying(undefined)}>Cancel</Button>
            </div>
          </form>
        </>}
      </ModalDialog>
    </Modal>
  )
}

type DeleteTagModalProps = {
  deleting: tag | undefined
  setDeleting: Function
  submitFunction: () => any
}

function DeleteTagModal({ deleting, setDeleting, submitFunction }: DeleteTagModalProps) {
  return (
    <Modal open={!!deleting} onClose={() => setDeleting(undefined)}>
      <ModalDialog>
        <ModalClose/>
        {deleting && <>
          Delete Tag
          <br/>
          <p>Are you sure you want to delete <code><b>!{deleting.tag}</b></code>?</p>
          <Button onClick={() => setDeleting(undefined)} variant="outlined">Cancel</Button>
          <br/>
          <Button onClick={submitFunction} color="danger">Delete</Button>
        </>}
      </ModalDialog>
    </Modal>
  )
}

export default function Tags() {
  const perPage = 30;
  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(ToastContext)
  const [loading, setLoading] = useState(true)

  const [page, setPage] = useState(0);
  const [fullTags, setFullTags] = useState<tag[]>(placeholderTags());
  const [displaying, setDisplaying] = useState<tag[]>(placeholderTags());

  const [modifying, setModifying] = useState<tag>();
  const [deleting, setDeleting] = useState<tag>()

  useEffect(() => {
    if (!user) return;
    tagAPI.get().then(info => {
      if (!setData(info, setToast, user, setUser)) return;

      setLoading(false);
      setPage(0)
      setFullTags(info);
    })
  }, [setToast, setUser, user])

  useEffect(() => {
    // when stuff is loaded
    if (fullTags) {
      // get the last page number
      const maxPage = Math.ceil(fullTags.length / perPage);

      // ensure boundaries are kept
      if (page > maxPage) setPage(maxPage - 1);
      if (page < 0) setPage(0);

      // show the data
      setDisplaying(fullTags.slice(page * perPage, ((page + 1) * perPage)));
    }
  }, [page, fullTags]);

  function changePage(num: number) {
    if (num > maxPage || num < 0) return;
    setPage(num);
    document.getElementById("topOfPage")?.scrollIntoView({ behavior: "smooth" })
  }

  if (!user || !user.perms.isMgr) return (<div/>);
  const maxPage = Math.ceil(fullTags.length / perPage) - 1

  const Navigation = () =>
  <div className="flexMidVert" style={{ justifyContent: "center" }}>
    <IconButton aria-label="First Page" disabled={page === 0} onClick={() => changePage(0)}><FirstPage/></IconButton>
    <IconButton aria-label="Previous Page" disabled={page === 0} onClick={() => changePage(page - 1)}><NavigateBefore/></IconButton>

    <Typography fontWeight="bold" sx={{ m: "0px 5px"}}>{page + 1}</Typography>

    <IconButton aria-label="Next Page" disabled={page === maxPage} onClick={() => changePage(page + 1)}><NavigateNext/></IconButton>
    <IconButton aria-label="Last Page" disabled={page === maxPage} onClick={() => changePage(maxPage)}><LastPage/></IconButton>
  </div>

  function onModify(e: any, attachment: attachment) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const obj = Object.fromEntries(formData.entries()) as any;

    if (!obj.tag) return setToast({ text: "A tag name is required", color: "warning" });

    const deleteFile = (!attachment.src && modifying?.attachment) ? "DELETE" : "";
    formData.set("deleteFile", deleteFile)

    if (!obj.response && (!obj.file || obj.file.size === 0)) return setToast({ text: "A response and/or attachment is required", color: "warning" });

    if (obj.file?.size === 0 || deleteFile) formData.delete("file");

    tagAPI.manage(formData).then((tag) => {
      if (!setData(tag, setToast, user, setUser)) return;

      const tags = [...fullTags];
      const index = tags.findIndex(t => t.tag.toLowerCase() === tag.tag.toLowerCase());
      if (index !== -1) tags[index] = tag;
      else tags.push(tag);
      setFullTags(tags);

      setModifying(undefined);
      setToast({ text: "The tag has been saved!", color: "success" });
    })

  }

  function onDelete() {
    if (!deleting) return;
    tagAPI.delete(deleting.tag.toLowerCase()).then(tags => {
      if (!setData(tags, setToast, user, setUser)) return;

      setFullTags(tags);
      setDeleting(undefined);
      setToast({ text: "The tag has been deleted!", color: "success" });
    });
  }

  return (
    <div className="contentRoot">
      <MetaProps meta={{
        splash: "icarus_daylight",
        title: "Tags",
        url: "tags",
        description: "Manage tags in the server"
      }}
      />
      <EditTagModal modifying={modifying} setModifying={setModifying} submitFunction={onModify}/>
      <DeleteTagModal deleting={deleting} setDeleting={setDeleting} submitFunction={onDelete}/>

      <h1 className="pageTitle" id="topOfPage">Tag Management</h1>
      <Navigation/>
      <div style={{ width: "100%", display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
        {
          displaying.map(t => {
            return (
            <Card key={t.id} sx={{  margin: "10px", width: "332px" }}>
              <Typography level="title-lg">
                <Skeleton loading={loading}>!{t.tag}</Skeleton>
              </Typography>

              <CardContent sx={{ width: "300px", overflowY: "auto", wordBreak: "break-word" }}>
                <Skeleton variant="text" loading={loading}>
                  {t.response && <Markdown>{t.response.replace(/\n/g, "<br/>").replace(/<a?:[^:]*:(\d*)>/g, '<img src="https://cdn.discordapp.com/emojis/$1.png?size=16" height="16px" width="16px" style="objectFit: contain"/>')}</Markdown>}
                </Skeleton>
              </CardContent>

              <AttachmentDisplay tag={t}/>

              <CardActions>
                <Button startDecorator={<ModeEdit/>} onClick={() => setModifying(t)}>Edit</Button>
                <Button startDecorator={<Delete/>} color="danger" onClick={() => setDeleting(t)}>Delete</Button>
              </CardActions>
            </Card>
          )})
        }
      </div>
      <Navigation/>
    </div>
  )
}

function AttachmentDisplay({ tag: t, path, mime }: { tag: tag, path?: string, mime?: string | null }) {

  const src = path || (t.attachment ? imgSrc("tags/" + t.id) : undefined);
  if (!src) return <></>

  mime ??= t.attachmentMime || "";
  if (mime.startsWith("video")) {
    return <video controls height={250} width={300} style={{ objectFit: "contain" }}>
      <source src={src} type={mime}/>
    </video>
  }

  if (mime.startsWith("image")) return <img src={src} alt="" height={250} width={300} style={{ objectFit: "contain" }}/>
  if (mime.startsWith("audio")) return <audio controls src={src}/>
  return <>No preview for {t.attachment}</>
}